"use client";

/**
 * Wrapper minimaliste pour tracker un clic CTA via /api/track/event
 * fire-and-forget. N'altère pas le comportement natif de l'enfant
 * (link <a>, button, etc.).
 *
 * Usage :
 *   <TrackedAction event="PHONE_CLICK" entitySlug={doc.slug} source="fiche-cta">
 *     <a href="tel:0600000000">Appeler</a>
 *   </TrackedAction>
 *
 * Le tracking est SYNC côté client (fetch keepalive=true) → garantit
 * l'envoi même si l'utilisateur quitte la page (cas tel: qui ouvre
 * l'app téléphone et perd le contexte navigateur).
 *
 * Plausible/GA4 (bilan 2026-10-21) : le lien d'appel des fiches à RDV en ligne
 * actif (Gold, réservés…) passait par ce composant, qui n'écrivait qu'en base :
 * ces appels manquaient dans GA4. Un PHONE_CLICK émet désormais
 * physician:call_intent puis physician:contact_phone, avec les paramètres du
 * mode direct d'UnclaimedCallButton. Cf. trackedActionAnalyticsEvents.
 */

import { cloneElement, isValidElement, type ReactElement } from "react";
import { track, type SahhaEvent } from "@/lib/observability/analytics";

type EventType =
  | "PHONE_CLICK"
  | "WHATSAPP_CLICK"
  | "EMAIL_CLICK"
  | "MAP_CLICK"
  | "RDV_INTENT"
  | "CLAIM_INTENT"
  | "FAVORITE_ADD"
  | "FAVORITE_REMOVE"
  | "SHARE_CLICK"
  | "REVIEW_INTENT"
  | "CONTACT_SUBMIT"
  | "PRO_INSCRIPTION_INTENT"
  | "TELECONSULT_INTENT"
  | "DOWNLOAD_APP_CLICK";

type AnalyticsProps = Record<string, string | undefined>;

/**
 * Types qui émettent Plausible/GA4 sans qu'on le demande. WHATSAPP_CLICK n'y est
 * pas : son seul usage, RdvContactLink, émet déjà physician:contact_whatsapp
 * depuis son lien enfant (le doubler compterait chaque message deux fois).
 */
const ANALYTICS_BY_DEFAULT: ReadonlySet<EventType> = new Set(["PHONE_CLICK"]);

type TrackedActionTarget = {
  event: EventType;
  entitySlug?: string;
  source?: string;
  metadata?: Record<string, unknown>;
};

function metadataString(metadata: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = metadata?.[key];
  return typeof value === "string" && value ? value : undefined;
}

/**
 * Événements Plausible/GA4 d'un clic, mêmes noms et mêmes paramètres que les
 * autres boutons de contact (UnclaimedCallButton, RdvContactLink,
 * StickyBookingCTA). Aucun pour les autres types d'action : pas d'événement
 * GA4 défini pour l'itinéraire, le partage ou l'intention de RDV.
 */
export function trackedActionAnalyticsEvents({
  event,
  entitySlug,
  source,
  metadata,
}: TrackedActionTarget): Array<[SahhaEvent, AnalyticsProps]> {
  switch (event) {
    case "PHONE_CLICK":
      return [
        ["physician:call_intent", { source, physician_slug: entitySlug }],
        [
          "physician:contact_phone",
          {
            source,
            physician_slug: entitySlug,
            tier: metadataString(metadata, "tier"),
            trial_boosted: metadataString(metadata, "trialBoosted"),
            city: metadataString(metadata, "city"),
          },
        ],
      ];
    case "WHATSAPP_CLICK":
      return [
        [
          "physician:contact_whatsapp",
          {
            source,
            physician_slug: entitySlug,
            tier: metadataString(metadata, "tier"),
            city: metadataString(metadata, "city"),
          },
        ],
      ];
    default:
      return [];
  }
}

type Props = {
  event: EventType;
  entityType?: string;
  entityId?: string;
  entitySlug?: string;
  source?: string;
  metadata?: Record<string, unknown>;
  /**
   * Émettre aussi l'événement Plausible/GA4 du clic. Par défaut : oui pour
   * PHONE_CLICK, non sinon. Passer false si l'enfant appelle déjà track(),
   * true pour un WHATSAPP_CLICK dont l'enfant ne l'émet pas.
   */
  analytics?: boolean;
  children: ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
};

/** Même envoi que les boutons d'appel : sendBeacon survit au départ vers le composeur. */
function sendTrackEvent(payload: string): void {
  try {
    // navigator.sendBeacon est l'API CONÇUE pour le tracking de clic : le
    // navigateur met la requête en file et garantit l'envoi même si la page
    // navigue dans le même tick (Link SPA Next, tel:, wa.me, maps:).
    // fetch+keepalive échouait pour les <Link> Next (la nav SPA coupait la
    // requête → RDV_INTENT/WHATSAPP_CLICK jamais enregistrés). Fallback fetch.
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const ok = navigator.sendBeacon("/api/track/event", new Blob([payload], { type: "application/json" }));
      if (ok) return;
    }
    fetch("/api/track/event", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {});
  } catch {
    // ignore
  }
}

export function TrackedAction({
  event,
  entityType,
  entityId,
  entitySlug,
  source,
  metadata,
  analytics = ANALYTICS_BY_DEFAULT.has(event),
  children,
}: Props) {
  if (!isValidElement(children)) return children;

  const handleClick = (e: React.MouseEvent) => {
    // Préserve le handler original
    const original = children.props.onClick;
    if (original) original(e);

    // Fire-and-forget : base d'abord, puis Plausible/GA4 (ordre d'UnclaimedCallButton).
    sendTrackEvent(JSON.stringify({ type: event, entityType, entityId, entitySlug, source, metadata }));
    if (!analytics) return;
    for (const [name, props] of trackedActionAnalyticsEvents({ event, entitySlug, source, metadata })) {
      track(name, props);
    }
  };

  return cloneElement(children, { onClick: handleClick });
}
